import { type DOMOutputSpec, Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, Selection as ProseSelection, Transaction } from "prosemirror-state";
import type { NodeView } from "prosemirror-view";
import uuid from "uuid";

import { nodeAttrsToStatus } from "~/actionItems/utils";

import { NodeExtension } from "../extension";

export default class MentionNode extends NodeExtension {
	get name() {
		return "mention";
	}
	view = (node: ProseMirrorNode): NodeView => {
		const dom = document.createElement("span");
		dom.setAttribute("data-id", node.attrs.id);
		dom.setAttribute("data-entity-id", node.attrs.entityId);
		dom.classList.add("mention-node");
		const { id: nodeUserId } = node.attrs;
		const { currentUserId } = this.options;
		if (currentUserId && nodeUserId === currentUserId) {
			dom.classList.add("mention-node__current-user");
		}
		dom.innerText = `@${node.textContent}`;
		return {
			dom,
			update: newNode => {
				if (newNode !== node) {
					return false;
				}
				dom.innerText = `@${node.textContent}`;
				return true;
			},
			destroy: () => {
				dom.remove();
			},
		};
	};
	get node() {
		return {
			group: "inline",
			content: "text{0,1}",
			inline: true,
			atom: true,
			isolating: true,
			marks: "",

			attrs: {
				id: { default: "" },
				entityId: { default: "" },
			},

			selectable: false,
			draggable: false,

			toDOM(node: ProseMirrorNode): DOMOutputSpec {
				// toDOM is not used in regular editor (nodeView takes precedence),
				// but is used in history view when calling `DOMSerializer`
				return [
					"span",
					{
						"data-mention": "",
						"data-id": node.attrs.id,
						"data-entity-id": node.attrs.entityId,
						class: "mention-node",
					},
					`@${node.textContent}`,
				];
			},
			parseDOM: [
				{
					tag: "span.mention-node[data-id]",
					getAttrs: (dom: Node | string) => {
						if (!(dom instanceof Element)) return null;
						return {
							id: dom.getAttribute("data-id"),
							entityId: uuid.v4(),
						};
					},
					contentElement: (dom: Node) => {
						const clone = dom.cloneNode(true) as HTMLElement;
						if (!(clone instanceof HTMLSpanElement)) return dom as HTMLElement;
						if (!clone.textContent) return dom as HTMLElement;
						if (clone.textContent.charAt(0) === "@") {
							clone.innerText = clone.innerText.substring(1);
						}
						return clone;
					},
				},
			],
		};
	}
	keys() {
		return {
			Backspace: (state: EditorState, dispatch?: (tr: Transaction) => void) => {
				const { selection } = state;
				if (!selection.empty) return false;
				const { from, $from } = selection;
				const nodeBefore = $from.nodeBefore;
				if (!nodeBefore) return false;
				if (nodeBefore.type !== this.$type || nodeBefore.childCount !== 1) return false;
				const textChild = nodeBefore.lastChild;
				if (!textChild || !textChild.text || textChild.text.split(" ").length <= 1) return false;
				if (dispatch) {
					const newText = textChild.text.split(" ").slice(0, -1).join(" ").trimRight();
					const removedLength = textChild.text.length - newText.length;
					if (!removedLength) return false;
					const tr = state.tr;
					tr.deleteRange(from - 1 - removedLength, from - 1);
					dispatch(tr);
				}
				return true;
			},
		};
	}
	static insertMention =
		({ id, name, invitable }: { id: string; name: string; invitable: boolean }, selection?: ProseSelection | null) =>
		(state: EditorState, dispatch?: (tr: Transaction) => void) => {
			const entityId = uuid.v4();
			const node: ProseMirrorNode = state.schema.nodes["mention"].create({ id, entityId }, state.schema.text(name));
			const sel = selection || state.selection;
			const { $from, $to, head } = sel;
			if (!$to.sameParent($from)) return false;
			if (dispatch) {
				let tr = state.tr;
				tr.replaceRangeWith(sel.from, sel.to, node);
				const parent = $from.parent;
				// mention to assignee
				if (typeof parent.attrs.assignees !== "undefined") {
					let hasMention = false;
					parent.descendants(node => {
						if (hasMention) return;
						if (node.type.name === "mention") {
							hasMention = true;
						}
					});
					if (
						(!hasMention || parent.attrs.assignees.length === 0) &&
						parent.attrs.assignees.length <= 1 &&
						!invitable
					) {
						tr = tr.setNodeMarkup(tr.mapping.map(head, 1) - node.nodeSize - $from.parentOffset - 1, undefined, {
							...parent.attrs,
							assignees: [{ id: id, status: nodeAttrsToStatus(parent.attrs) }],
						});
					}
				}
				dispatch(tr);
			}
			return true;
		};
}
